import type {
  RenderRunActivity,
  RenderStartVerification,
} from "@/features/credentials/components/primary-source-runs/types";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useTRPC, type RouterOutputs } from "@/trpc";
import { ProviderInitialsAvatar } from "@/features/credentials/components/provider-initials-avatar";
import { CredentialVaultPanel } from "@/features/credentials/components/credential-vault-panel";
import type { CredentialVaultTab } from "@/features/credentials/components/credential-vault-view";
import { CredentialPanelSkeleton } from "@/features/credentials/components/credential-panel-skeleton";
import { useRunHistory } from "@/features/credentials/components/primary-source-runs/use-run-history";
import { PrimarySourceRuns } from "@/features/credentials/components/primary-source-runs/primary-source-runs";
import { useOrgCredentialEditing } from "@/features/credentials/hooks/use-org-credential-editing";
import { useProviderCredentialImports } from "@/features/credentials/hooks/use-provider-credential-imports";
import { ProviderImportContext } from "@/features/credentials/lib/provider-import-context";
import type { ProviderCredentialSummary } from "./classify";

type ProviderCredentialsSheetProps = {
  provider: ProviderCredentialSummary | null;
  providerUserId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationBusinessUnitId: string;
  primarySourceVerificationEnabled: boolean;
  renderRunActivity?: RenderRunActivity;
  renderStartVerification?: RenderStartVerification;
  credentialViewId?: string;
  expectedAffiliationId?: string;
  onEditPersonalDetails?: () => void;
  personalDetailsContent?: ReactNode;
  headerActions?: ReactNode;
  additionalDialogOpen?: boolean;
  /** Caller-owned data that must finish loading before the panel is revealed. */
  isDetailsLoading?: boolean;
  /** Set when this sheet replaces a loading panel that is already open. */
  skipEntryAnimation?: boolean;
  /** Show the credential record inline on a page. Verification runs live on
   * the page's own tab, so the record omits them. */
  embedded?: boolean;
  /** The view an embedded record shows, chosen by the page's tabs. */
  view?: CredentialVaultTab;
  children?: ReactNode;
};

/** New-model org entrypoint; the flag-off legacy sheet is intentionally separate. */
export function ProviderCredentialsSheetV2(
  props: ProviderCredentialsSheetProps,
) {
  // Navigation to another record discards old drafts, uploads and signed URLs.
  return (
    <ScopedProviderCredentialsSheet
      key={`${props.organizationBusinessUnitId}:${props.providerUserId ?? ""}:${props.credentialViewId ?? "current"}:${props.expectedAffiliationId ?? ""}`}
      {...props}
    />
  );
}

function ScopedProviderCredentialsSheet({
  provider,
  providerUserId,
  open,
  onOpenChange,
  organizationBusinessUnitId,
  primarySourceVerificationEnabled,
  renderRunActivity,
  renderStartVerification,
  credentialViewId,
  expectedAffiliationId,
  onEditPersonalDetails,
  personalDetailsContent,
  headerActions,
  additionalDialogOpen = false,
  isDetailsLoading = false,
  skipEntryAnimation = false,
  embedded = false,
  view,
  children,
}: ProviderCredentialsSheetProps) {
  const trpc = useTRPC();
  const query = useQuery({
    ...trpc.credentials.getForBu.queryOptions({
      organizationBusinessUnitId,
      providerUserId: providerUserId ?? "",
      credentialViewId,
      expectedAffiliationId,
    }),
    enabled: open && !!providerUserId,
  });
  const data = expectedAffiliationId && query.isError ? undefined : query.data;
  const editing = useOrgCredentialEditing({
    organizationBusinessUnitId,
    providerUserId: providerUserId ?? "",
    data,
    enabled: open && !!providerUserId,
    credentialViewId,
    expectedAffiliationId,
    onEditPersonalDetails: data?.access.canEdit
      ? undefined
      : onEditPersonalDetails,
    personalDetailsContent,
  });
  const imports = useProviderCredentialImports({
    scope: {
      organizationBusinessUnitId,
      providerUserId: providerUserId ?? "",
      ...(expectedAffiliationId ? { expectedAffiliationId } : {}),
    },
    enabled:
      open &&
      !!providerUserId &&
      !credentialViewId &&
      !!data?.access.canViewProviderOffers,
    canEdit: data?.access.canEdit ?? false,
    disabled: editing.isDialogOpen || additionalDialogOpen,
  });
  // Start the runs history alongside the record so the panel appears once,
  // complete. A history error must not block the panel; the runs section
  // shows its own retry.
  const runHistory = useRunHistory({
    scope: {
      organizationBusinessUnitId,
      providerUserId: providerUserId ?? "",
      expectedAffiliationId: credentialViewId
        ? undefined
        : expectedAffiliationId,
      credentialViewId,
    },
    enabled:
      open && primarySourceVerificationEnabled && !embedded && !!providerUserId,
  });
  const isRunHistoryLoading =
    primarySourceVerificationEnabled &&
    !embedded &&
    !!providerUserId &&
    runHistory.isPending;
  const isReady = !!data && !isDetailsLoading && !isRunHistoryLoading;
  const name = providerName(provider, data);
  const subtitle = [provider?.role, provider?.specialty]
    .filter(Boolean)
    .join(" · ");

  function changeOpen(next: boolean) {
    if (
      !next &&
      (editing.isDialogOpen || imports.isOpen || additionalDialogOpen)
    ) {
      return;
    }
    if (!next) {
      editing.close();
    }
    onOpenChange(next);
  }

  const recordFallback = query.isError ? (
    <div role="alert" className="space-y-3 p-6 text-sm text-slate-600">
      <p>Couldn’t load credential details.</p>
      <Button variant="outline" onClick={() => void query.refetch()}>
        Try again
      </Button>
    </div>
  ) : (
    <CredentialPanelSkeleton
      showPrimarySourceRuns={primarySourceVerificationEnabled && !embedded}
    />
  );

  return (
    <ProviderImportContext.Provider value={imports.context}>
      <CredentialVaultPanel
        userId={providerUserId ?? ""}
        data={isReady && data ? data : null}
        open={open}
        onOpenChange={changeOpen}
        title={name}
        dialogTitle={`${name} credentials`}
        description={subtitle || null}
        headerActions={headerActions}
        headerIcon={<ProviderInitialsAvatar name={name} />}
        credentialsHeading={
          credentialViewId
            ? "Credentials from the previous affiliation"
            : "Credentials on record"
        }
        recordFallback={recordFallback}
        skipEntryAnimation={skipEntryAnimation}
        embedded={embedded}
        view={view}
        cardActions={editing.cardActions}
        headerNotice={
          imports.notice ?? (!data?.access.canEdit ? imports.action : null)
        }
        notice={editing.notice}
        filesView={editing.filesView}
        closeDisabled={
          editing.isDialogOpen || imports.isOpen || additionalDialogOpen
        }
        summaryActions={editing.summaryActions}
        uploadArea={editing.uploadArea}
        sectionContent={editing.sectionContent}
        sectionBodies={editing.sectionBodies}
        credentialsHeader={
          primarySourceVerificationEnabled &&
          !embedded &&
          providerUserId &&
          data ? (
            <PrimarySourceRuns
              renderRunActivity={renderRunActivity}
              renderStartVerification={renderStartVerification}
              organizationBusinessUnitId={organizationBusinessUnitId}
              providerUserId={providerUserId}
              expectedAffiliationId={
                credentialViewId ? undefined : expectedAffiliationId
              }
              credentialViewId={credentialViewId}
              providerName={name}
              canEdit={data.access.canEdit}
              open={open}
            />
          ) : null
        }
      >
        {editing.modals}
        {imports.dialog}
        {children}
      </CredentialVaultPanel>
    </ProviderImportContext.Provider>
  );
}

function providerName(
  provider: ProviderCredentialSummary | null,
  data: RouterOutputs["credentials"]["getForBu"] | undefined,
) {
  const fromRow = [provider?.firstName, provider?.lastName]
    .filter(Boolean)
    .join(" ");
  if (fromRow) {
    return fromRow;
  }
  return (
    [data?.provider.firstName, data?.provider.lastName]
      .filter(Boolean)
      .join(" ") || "This professional"
  );
}
